import Planimpfung from '@/types/Planimpfung';
import PatUntersuchung from '@/types/PatUntersuchung';

export default class Reiseplan {
    public patplan_id: number | null = null;
    public patnr: number | null = null;
    public datum: string | null = null;
    public reiseziel_id: number | null = null;
    public reiseart_id: number | null = null;
    public abreisedatum: string | null = null;
    public reisedauer: number | null = null;
    public bemerkungen: string | null = null;
    public koerpergroesse: string | null = '';
    public koerpergewicht: string | null = '';
    public person_id_durchf: number | null = null;
    public rz_reiseziel: string | null = '';
    public ra_reiseart: string | null = '';
    public reiseziele: number[] = [];
    public reiseziele_text: string[] = [];

    public get text(): string {
        const text: string[] = [];

        if (this.reiseziele_text.length > 0) {
            text.push(this.reiseziele_text.join(' ').slice(0, 20));
        }

        if (this.ra_reiseart && this.ra_reiseart.length > 0) {
            text.push(this.ra_reiseart.slice(0, 20));
        }

        if (this.bemerkungen && this.bemerkungen.length > 0) {
            text.push(this.bemerkungen.slice(0, 20));
        }

        return text.join(' / ');
    }

    constructor(data?: Partial<Reiseplan>) {
        Object.assign(this, data);
    }
}

export class PlanItem<T extends Planimpfung | PatUntersuchung> {
    public data: T;
    public tag = '';
    public id = '';

    public get isImpfung(): boolean {
        return this.data instanceof Planimpfung;
    }

    public get isUntersuchung(): boolean {
        return this.data instanceof PatUntersuchung;
    }

    public get asImpfung(): Planimpfung {
        return this.data as Planimpfung;
    }

    public get asUntersuchung(): PatUntersuchung {
        return this.data as PatUntersuchung;
    }

    public get art(): string {
        if (this.isImpfung) {
            return 'Impfung';
        } else if (this.isUntersuchung) {
            return 'Untersuchung';
        }

        return '';
    }

    public get isDisabled(): boolean {
        if (this.isImpfung) {
            //return (this.data as Planimpfung).geimpft === 'j';
            return ((this.data as Planimpfung).impfdatum !== null) || ((this.data as Planimpfung).geimpft === 'j');
        } else if (this.isUntersuchung) {
            return !!(this.data as PatUntersuchung).abnahmedatum;
        }

        return true;
    }

    public get durchgefuehrt(): string | null {
        if (this.isImpfung) {
            return (this.data as Planimpfung).impfdatum;
        } else if (this.isUntersuchung) {
            return (this.data as PatUntersuchung).untersuchungsdatum;
        }

        return '';
    }

    public get datumGeplant(): string | null {
        if (this.isImpfung) {
            return (this.data as Planimpfung).impfdatum_geplant;
        } else if (this.isUntersuchung) {
            return (this.data as PatUntersuchung).geplant ?? (this.data as PatUntersuchung).abnahmedatum;
        }

        return '';
    }

    public set datumGeplant(datum: string | null) {
        if (this.isImpfung) {
            (this.data as Planimpfung).impfdatum_geplant = datum;
        } else if (this.isUntersuchung) {
            (this.data as PatUntersuchung).geplant = datum;
        }
    }

    public get bezeichnung(): string | null {
        if (this.isImpfung) {
            return (this.data as Planimpfung).impfungsText;
        } else if (this.isUntersuchung) {
            return (this.data as PatUntersuchung).untersuchung;
        }

        return '';
    }

    public get displayText(): string {
        if (this.isImpfung) {
            return (this.data as Planimpfung).teilimpfungsKuerzel + (this.data as Planimpfung).impfungsText;
        } else if (this.isUntersuchung) {
            return (this.data as PatUntersuchung).untersuchung ?? '';
        }

        return '';
    }

    public get datumDurchgefuehrt(): string | null {
        if (this.isImpfung) {
            return (this.data as Planimpfung).impfdatum;
        } else if (this.isUntersuchung) {
            return (this.data as PatUntersuchung).abnahmedatum;
        }

        return '';
    }

    public get planDatum(): string | null {
        if (this.isImpfung) {
            return (this.data as Planimpfung).plan_datum;
        } else if (this.isUntersuchung) {
            return (this.data as PatUntersuchung).plan_datum;
        }

        return '';
    }

    constructor(data: T) {
        this.data = data;
        this.tag = this.art.toLowerCase();

        if (this.isImpfung) {
            const { patplanimpfstoff_id, impfstoff_id, teilimpfungsnr } = this.data as Planimpfung;
            this.id = patplanimpfstoff_id?.toString() ?? `${impfstoff_id}-${teilimpfungsnr}-${Date.now()}`;
        } else if (this.isUntersuchung) {
            const { patuntersuchung_id, untersuchung_id } = this.data as PatUntersuchung;
            this.id = patuntersuchung_id?.toString() ?? `${untersuchung_id}-${Date.now()}`;
        }
    }

    static from<T extends Planimpfung | PatUntersuchung>(data: T): PlanItem<T> {
        return new PlanItem<T>(data);
    }

    public clone(): PlanItem<T> {
        return new PlanItem<T>(this.data);
    }
}
